import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Container } from "../components/Container";
import { SEO } from "../components/SEO";
import { BRAND } from "../lib/constants";

type FormState = "idle" | "submitting" | "success" | "error";

export function DeleteAccount() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    emailRef.current?.focus();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setFormState("submitting");
    try {
      const mailtoBody = encodeURIComponent(
        `Account deletion request\n\nEmail: ${email.trim()}\nReason: ${reason.trim() || "Not specified"}\n\nPlease delete my ALBAVOX account and all associated personal data within 30 days as required by applicable privacy law.`,
      );
      const subject = encodeURIComponent("Account Deletion Request – ALBAVOX");
      window.location.href = `mailto:${BRAND.email}?subject=${subject}&body=${mailtoBody}`;
      // After a short delay treat it as "submitted" so the user sees confirmation
      await new Promise((r) => setTimeout(r, 800));
      setFormState("success");
    } catch {
      setFormState("error");
    }
  }

  return (
    <main className="min-h-screen pt-28 sm:pt-32">
      <SEO
        title={t("delete_account.meta_title")}
        description={t("delete_account.meta_description")}
        path="/delete-account"
      />

      <Container size="md">
        <article className="py-12 sm:py-16">
          <header className="max-w-2xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-brand-500">
              {t("delete_account.eyebrow")}
            </p>
            <h1 className="hero-headline text-text-heading">
              {t("delete_account.title")}
            </h1>
            <p className="mt-4 text-lg text-text-muted">
              {t("delete_account.subtitle")}
            </p>
          </header>

          {formState === "success" ? (
            <div className="mt-10 rounded-2xl border border-border-soft bg-bg-subtle p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-500">
                {t("delete_account.success_eyebrow")}
              </p>
              <h2 className="mt-2 text-2xl font-bold text-text-heading">
                {t("delete_account.success_title")}
              </h2>
              <p className="mt-3 text-text-muted">
                {t("delete_account.success_body")}
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mt-10 max-w-lg space-y-6"
              noValidate
            >
              {/* What happens */}
              <div className="rounded-xl border border-border-soft bg-bg-subtle px-5 py-4 text-sm text-text-muted">
                {t("delete_account.info")}
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="da-email"
                  className="mb-1.5 block text-sm font-medium text-text-heading"
                >
                  {t("delete_account.email_label")}
                  <span className="ml-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  ref={emailRef}
                  id="da-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("delete_account.email_placeholder")}
                  className="w-full rounded-xl border border-border-soft bg-surface px-4 py-3 text-sm text-text-heading placeholder:text-text-subtle focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              {/* Optional reason */}
              <div>
                <label
                  htmlFor="da-reason"
                  className="mb-1.5 block text-sm font-medium text-text-heading"
                >
                  {t("delete_account.reason_label")}
                  <span className="ml-1 text-xs text-text-subtle">
                    ({t("delete_account.optional")})
                  </span>
                </label>
                <textarea
                  id="da-reason"
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t("delete_account.reason_placeholder")}
                  className="w-full rounded-xl border border-border-soft bg-surface px-4 py-3 text-sm text-text-heading placeholder:text-text-subtle focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              {formState === "error" && (
                <p className="text-sm text-red-500">
                  {t("delete_account.error")}
                </p>
              )}

              <button
                type="submit"
                disabled={formState === "submitting" || !email.trim()}
                className="inline-flex w-full items-center justify-center rounded-full bg-red-600 px-6 py-3 font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {formState === "submitting"
                  ? t("delete_account.submitting")
                  : t("delete_account.submit")}
              </button>

              <p className="text-xs text-text-subtle">
                {t("delete_account.privacy_note", { email: BRAND.email })}
              </p>
            </form>
          )}

          {/* Alternative: open app */}
          <div className="mt-12 border-t border-border-soft pt-10">
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-text-subtle">
              {t("delete_account.in_app_heading")}
            </h2>
            <p className="mt-2 text-sm text-text-muted">
              {t("delete_account.in_app_body")}
            </p>
            <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-text-muted">
              <li>{t("delete_account.in_app_step1")}</li>
              <li>{t("delete_account.in_app_step2")}</li>
              <li>{t("delete_account.in_app_step3")}</li>
            </ol>
          </div>
        </article>
      </Container>
    </main>
  );
}
